import { BehaviorSubject } from 'rxjs';

export class UpdateService {
	private readonly STORAGE_KEY = 'last_updated_version';
	private readonly updateAvailable$ = new BehaviorSubject<boolean>(false);
	private readonly currentVersion: string;
	private readonly baseInterval = 30 * 1000;
	private readonly maxInterval = 30 * 60 * 1000;
	private currentInterval: number;
	private checkTimer: number | undefined;
	private consecutiveErrors = 0;
	private notifiedVersion: string | null = null;
	private lastCheckedTimestamp: string | null = null;
	private swRegistration: ServiceWorkerRegistration | null = null;
	private isUpdateInProgress = false;

	constructor() {
		this.currentVersion = import.meta.env.VITE_APP_VERSION || '0.0.0';
		this.currentInterval = this.baseInterval;

		/* console.log('UpdateService initialized:', {
			currentVersion: this.currentVersion,
			env: import.meta.env,
		}); */

		void this.setupServiceWorker();
		this.setupNetworkListeners();
		void this.init();
	}

	public getUpdateAvailable(): BehaviorSubject<boolean> {
		return this.updateAvailable$;
	}

	public async forceUpdate(): Promise<void> {
		if (this.isUpdateInProgress) {
			// console.log('Update already in progress, ignoring request');
			return;
		}

		this.isUpdateInProgress = true;
		// console.log('Forcing update...');

		try {
			// Get current version from meta.json before reloading
			const response = await fetch(`/meta.json?t=${new Date().getTime()}`, {
				cache: 'no-store',
				headers: {
					'Cache-Control': 'no-cache, no-store, must-revalidate',
					Pragma: 'no-cache',
					Expires: '0',
				},
			});

			const meta = await response.json();
			if (meta.version) {
				sessionStorage.setItem(this.STORAGE_KEY, meta.version);
			}

			// Activate new Service Worker if it exists
			if (this.swRegistration?.waiting) {
				this.swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
			} else {
				// If no Service Worker, force reload
				await this.clearCacheAndReload();
			}
		} catch (error) {
			console.error('Error during update:', error);
			this.isUpdateInProgress = false;
			window.location.reload();
		}
	}

	private async setupServiceWorker(): Promise<void> {
		if ('serviceWorker' in navigator) {
			try {
				// Service Worker is now automatically registered by Vite PWA
				this.swRegistration = await navigator.serviceWorker.ready;

				// Listen for Service Worker updates
				this.swRegistration.addEventListener('updatefound', () => {
					const newWorker = this.swRegistration?.installing;
					if (newWorker) {
						newWorker.addEventListener('statechange', () => {
							if (
								newWorker.state === 'installed' &&
								navigator.serviceWorker.controller
							) {
								// this.notifyUpdateAvailable();
							}
						});
					}
				});

				// Reload when new Service Worker takes control
				navigator.serviceWorker.addEventListener('controllerchange', () => {
					if (this.updateAvailable$.value) {
						// console.log('New service worker took control, reloading page');
						window.location.reload();
					}
				});

				// Check if there's already a waiting service worker
				if (this.swRegistration.waiting) {
					// console.log('Found waiting service worker, notifying user');
					this.notifyUpdateAvailable();
				}
			} catch (error) {
				console.error('Error registering Service Worker:', error);
			}
		}
	}

	private notifyUpdateAvailable(): void {
		// console.log('Notifying update available');
		this.updateAvailable$.next(true);
	}

	private async clearCacheAndReload(): Promise<void> {
		try {
			if ('caches' in window) {
				const cacheKeys = await caches.keys();
				await Promise.all(cacheKeys.map(async key => await caches.delete(key)));
			}
			window.location.reload();
		} catch (error) {
			// console.error('Error clearing cache:', error);
			window.location.reload();
		}
	}

	private setupNetworkListeners(): void {
		window.addEventListener('online', () => {
			// console.log('Connection restored, restarting checks...');
			this.resetInterval();
			void this.checkForUpdates();
		});

		window.addEventListener('offline', () => {
			// console.log('No connection, pausing checks...');
			this.pauseChecks();
		});
	}

	private resetInterval(): void {
		this.consecutiveErrors = 0;
		this.currentInterval = this.baseInterval;
		this.scheduleNextCheck();
	}

	private pauseChecks(): void {
		if (this.checkTimer) {
			window.clearInterval(this.checkTimer);
			this.checkTimer = undefined;
		}
	}

	private scheduleNextCheck(): void {
		this.pauseChecks();
		if (navigator.onLine) {
			this.checkTimer = window.setInterval(() => {
				void this.checkForUpdates();
			}, this.currentInterval);
		}
	}

	private calculateNextInterval(): void {
		this.currentInterval = Math.min(
			this.baseInterval * Math.pow(2, this.consecutiveErrors),
			this.maxInterval
		);
		// console.log(`Next check in ${this.currentInterval / 1000} seconds`);
	}

	private init(): void {
		// Check for updates immediately when the application starts
		if (navigator.onLine) {
			// console.log('Checking for updates on initialization');
			void this.checkForUpdates();
		}

		// Schedule regular checks
		this.scheduleNextCheck();

		// Add event listener for when the page becomes visible
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible' && navigator.onLine) {
				// console.log('Page became visible, checking for updates');
				void this.checkForUpdates();
			}
		});
	}

	private compareVersions(v1: string, v2: string): number {
		if (!v1 || !v2) return 0;

		const parts1 = v1.split('.').map(Number);
		const parts2 = v2.split('.').map(Number);

		/* console.log('Comparing versions:', {
			v1,
			v2,
			parts1,
			parts2,
		}); */

		for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
			const part1 = parts1[i] || 0;
			const part2 = parts2[i] || 0;

			if (part1 > part2) return 1;
			if (part1 < part2) return -1;
		}

		return 0;
	}

	private async checkForUpdates(): Promise<void> {
		if (!navigator.onLine || this.isUpdateInProgress) {
			return;
		}

		try {
			const response = await fetch(`/meta.json?t=${new Date().getTime()}`, {
				cache: 'no-store',
				headers: {
					'Cache-Control': 'no-cache, no-store, must-revalidate',
					Pragma: 'no-cache',
					Expires: '0',
				},
			});

			const meta = await response.json();

			// console.log('Meta.json received:', meta);
			/* console.log('Current state:', {
				metaVersion: meta.version,
				currentVersion: this.currentVersion,
				notifiedVersion: this.notifiedVersion,
				lastCheckedTimestamp: this.lastCheckedTimestamp,
				newTimestamp: meta.timestamp,
				lastUpdatedVersion: sessionStorage.getItem(this.STORAGE_KEY),
				updateAvailable: this.updateAvailable$.value,
				isUpdateInProgress: this.isUpdateInProgress,
			}); */

			if (!meta.version) {
				console.warn('No version found in meta.json');
				return;
			}

			// If timestamp is different, it means there was a new build
			const isNewBuild =
				!this.lastCheckedTimestamp ||
				meta.timestamp !== this.lastCheckedTimestamp;
			this.lastCheckedTimestamp = meta.timestamp;

			const comparison = this.compareVersions(
				meta.version,
				this.currentVersion
			);

			// Check if we already updated to this version
			const lastUpdatedVersion = sessionStorage.getItem(this.STORAGE_KEY);

			// If no version is saved in sessionStorage, save current version
			if (!lastUpdatedVersion) {
				/* console.log(
					'First time running the application, saving current version'
				); */
				sessionStorage.setItem(this.STORAGE_KEY, meta.version);
				return;
			}

			const hasBeenUpdated = lastUpdatedVersion === meta.version;

			if (
				(comparison > 0 || (comparison === 0 && isNewBuild)) &&
				!hasBeenUpdated &&
				!this.updateAvailable$.value
			) {
				/* console.log('New version or build detected:', {
					nueva: meta.version,
					actual: this.currentVersion,
					notificada: this.notifiedVersion,
					esNuevoBuild: isNewBuild,
					yaActualizada: hasBeenUpdated,
				}); */
				this.notifyUpdateAvailable();
				this.notifiedVersion = meta.version;
			}

			if (this.consecutiveErrors > 0) {
				this.resetInterval();
			}
		} catch (error) {
			console.error('Error checking for updates:', error);
			this.consecutiveErrors++;
			this.calculateNextInterval();
			this.scheduleNextCheck();
		}
	}
}
